import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

// Services
import { RouteParam, ServiceBase } from '../service-base';

// Modèles
import { AnalyticalDocumentStatus } from 'src/app/@shared/models/analytical/analytical-document-status.model';
import { AnalyticalVentilationState } from 'src/app/@shared/models/analytical/analytical-ventilation-state.model';
import { GeneralFactory } from 'src/app/@shared/models/factories/general-factory';

// Utilities
import { ODataResponse } from 'src/app/@shared/utilities/odata/response';

/** Ligne de ventilation envoyée à l'action replace (le pourcentage fait foi côté back). */
export interface AnalyticalDistributionEntry {
    section: number;
    percentage: number;
    allocationType: string;
    amount: number;
}

const CLASS_NAME = 'AnalyticalVentilationState';
const ENDPOINT = `/analytical-ventilations/`;
const ROUTES: RouteParam[] = [
    { key: 'documentStatus', path: 'document-status/' },
    { key: 'replace', path: 'replace/' },
    { key: 'reset', path: 'reset/' },
]

@Injectable({
    providedIn: 'root'
})
export class AnalyticalVentilationService extends ServiceBase {
    constructor() {
        super(CLASS_NAME, ENDPOINT);
        this.addRoutes(ROUTES);
    }

    /** Complétude analytique d'un document (badge « Analytique complète / incomplète »). */
    public getDocumentStatus$(documentType: string, documentId: number): Observable<AnalyticalDocumentStatus> {
        const params = new HttpParams()
            .set('documentType', documentType)
            .set('documentId', documentId.toString());
        return this.httpClient.get(this.routes.documentStatus, { params }).pipe(
            map((body: any) => GeneralFactory.get('AnalyticalDocumentStatus', {
                carriers: body.carriers,
                complete: body.complete
            }) as AnalyticalDocumentStatus),
            catchError((error: any) => {
                this.handleError(error);
                return of(null as any);
            })
        );
    }

    /** État analytique d'un porteur : axes déclenchés (équilibre) + ventilations. */
    public getState$(carrierType: string, carrierId: number): Observable<AnalyticalVentilationState> {
        const params = new HttpParams()
            .set('carrierType', carrierType)
            .set('carrierId', carrierId.toString());
        return this.httpClient.get(this.routes.list, { params }).pipe(
            map((body: any) => this._toState(body)),
            catchError((error: any) => {
                this.handleError(error);
                return of(null as any);
            })
        );
    }

    /** Remplacement complet, atomique, de la ventilation d'un porteur. */
    public replace$(carrierType: string, carrierId: number, distributions: AnalyticalDistributionEntry[]): Observable<AnalyticalVentilationState> {
        return this._putState$(this.routes.replace, {
            carrierType: carrierType,
            carrierId: carrierId,
            distributions: distributions
        });
    }

    /** Réinitialisation explicite : purge puis ré-alimentation par les défauts. */
    public reset$(carrierType: string, carrierId: number): Observable<AnalyticalVentilationState> {
        return this._putState$(this.routes.reset, {
            carrierType: carrierType,
            carrierId: carrierId
        });
    }

    /** PUT d'une action de ventilation : renvoie le nouvel état et affiche les messages du serveur. */
    private _putState$(url: string, body: any): Observable<AnalyticalVentilationState> {
        return this.httpClient.put(url, body).pipe(
            map((response: any) => {
                this.messageService.sendODataMessages(new ODataResponse(response).messages);
                const state = this._toState(response);
                // Permet aux badges / boutons de statut de se rafraîchir après chaque sauvegarde
                this._updated$.next(state);
                return state;
            }),
            catchError((error: any) => {
                this.handleError(error);
                return of(null as any);
            })
        );
    }

    /** L'état est renvoyé à la racine de la réponse (pas d'enveloppe value). */
    private _toState(body: any): AnalyticalVentilationState {
        return GeneralFactory.get(CLASS_NAME, {
            axes: body.axes,
            distributions: body.distributions
        }) as AnalyticalVentilationState;
    }
}
